<template>
  <div class="initial-book-new-data-wrapper" v-loading.fullscreen.lock="loading">
    <!-- 筛选区域 -->
    <div class="filter-section">
      <el-card shadow="hover">
        <div class="filter-content">
          <div class="filter-item">
            <label class="filter-label">初始小说：</label>
            <el-select
              v-model="bookId"
              filterable
              :loading="booksLoading"
              :placeholder="initialBooks.length ? '选择初始小说' : '该日期范围内无初始小说数据'"
              style="width: 300px;"
            >
              <el-option
                v-for="b in initialBooks"
                :key="b.game_id"
                :label="`${b.title}（${b.user_count}人）`"
                :value="b.game_id"
              />
            </el-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">N日内数据：</label>
            <el-select v-model="nDays" style="width: 130px;">
              <el-option v-for="o in nDaysOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">注册日期：</label>
            <el-date-picker
              v-model="dateRange"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              format="yyyy-MM-dd"
              value-format="yyyy-MM-dd"
              style="width: 260px;"
            />
          </div>

          <div class="filter-item">
            <label class="filter-label">渠道：</label>
            <el-select v-model="channel" style="width: 120px;">
              <el-option v-for="o in channelOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </div>

          <div class="filter-item">
            <label class="filter-label">版本：</label>
            <el-select v-model="versionFilter" style="width: 170px;">
              <el-option-group label="及之后">
                <el-option v-for="v in versionOptionList" :key="'gte:' + v" :label="v + ' 及之后'" :value="'gte:' + v" />
              </el-option-group>
              <el-option-group label="仅此版本">
                <el-option v-for="v in versionOptionList" :key="'eq:' + v" :label="'仅 ' + v" :value="'eq:' + v" />
              </el-option-group>
            </el-select>
          </div>

          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
        </div>
        <div class="filter-tip">
          <strong>本页仅统计 App 版本{{ versionTipText }}的用户，其他版本用户不计入；1.0.16 起注册需绑定手机号：无手机号的 1.0.16 及之后版本用户不算注册成功，不计入本页任何用户指标。1.0.16 前的用户无初始书记录，选低于 1.0.16 的版本时他们全部落入「非初始用户」列。</strong><br />
          「初始小说」= 用户注册后首启直连进入的第一本小说。三列为<strong>互斥的用户群拆分</strong>：<strong>初始用户</strong> = 以所选书为初始小说的新增用户（直连获客）；<strong>非初始用户</strong> = 其余新增用户（含 1.0.16 前无初始书记录的用户）；<strong>全部</strong> = 全体新增用户（前两列之和，人数见列头）。三列指标均为该人群<strong>跨全书</strong>的平台口径（与「平台新增数据」页同一套定义），对比「这本书拉来的用户 vs 其他用户」的整体质量。<br />
          日期范围 = 注册日期范围（圈定该期间新增的用户）；N日内 = 每个用户「注册当天(D0)起、到第 N 个自然日结束」窗口内的行为（0 日内 = 注册当天）。<br />
          进入剧情 = 该书产生过对白记录（业务数据口径，不依赖埋点，覆盖首启直连进书）；曝光/详情页为埋点口径，初始用户直连进书不经列表与详情页，其曝光/详情数据仅反映「回书城主动浏览」。
        </div>
      </el-card>
    </div>

    <!-- 三列对比结果 -->
    <div class="result-section">
      <h3 class="section-title">初始小说新增数据</h3>
      <div v-if="initData || nonInitData || allData" class="metric-groups">
        <div v-for="group in metricGroups" :key="group.title" class="metric-group">
          <h4 class="group-title">{{ group.title }}</h4>
          <el-table :data="group.metrics" size="small" stripe :header-cell-style="headerCellStyle">
            <el-table-column label="指标" min-width="220">
              <template slot-scope="scope"><span class="metric-name">{{ scope.row.label }}</span></template>
            </el-table-column>
            <el-table-column min-width="160" align="center">
              <template slot="header" slot-scope="{}"><span class="col-head col-head-init">初始用户{{ colCount(initData) }}</span></template>
              <template slot-scope="scope">
                <span :class="valueClass(scope.row)">{{ mainValue(scope.row, initData) }}</span>
                <span v-if="subCount(scope.row, initData)" class="cell-sub">{{ subCount(scope.row, initData) }}</span>
              </template>
            </el-table-column>
            <el-table-column min-width="160" align="center">
              <template slot="header" slot-scope="{}"><span class="col-head col-head-noninit">非初始用户{{ colCount(nonInitData) }}</span></template>
              <template slot-scope="scope">
                <span :class="valueClass(scope.row)">{{ mainValue(scope.row, nonInitData) }}</span>
                <span v-if="subCount(scope.row, nonInitData)" class="cell-sub">{{ subCount(scope.row, nonInitData) }}</span>
              </template>
            </el-table-column>
            <el-table-column min-width="160" align="center">
              <template slot="header" slot-scope="{}"><span class="col-head">全部{{ colCount(allData) }}</span></template>
              <template slot-scope="scope">
                <span :class="valueClass(scope.row)">{{ mainValue(scope.row, allData) }}</span>
                <span v-if="subCount(scope.row, allData)" class="cell-sub">{{ subCount(scope.row, allData) }}</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <div v-else class="empty-tip">请选择初始小说后点击查询</div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator'
import { PlatformNewData, getAppVersions } from '@/api/platformNewData'
import { getInitialBookNewData, getInitialBooks, InitialBookNewDataParams, InitialBookOption } from '@/api/initialBookNewData'

// 版本筛选默认值：复合值 'gte:1.0.16'（及之后）/ 'eq:1.0.16'（仅此版本），请求时拆成 version + version_mode
const DEFAULT_VERSION = '1.0.16'
const DEFAULT_VERSION_FILTER = `gte:${DEFAULT_VERSION}`

interface MetricDef {
  label: string
  type: 'count' | 'rate'
  field?: keyof PlatformNewData            // count 用
  num?: keyof PlatformNewData              // rate 分子
  den?: keyof PlatformNewData              // rate 分母
}

@Component
export default class InitialBookNewDataView extends Vue {
  // 仅用于吸收 router-view 透传的 prop，避免 attribute fallthrough，本页不使用
  @Prop() readonly game?: unknown
  @Prop() readonly games?: unknown

  loading = false
  bookId: number | null = null
  nDays: 0 | 1 | 3 | 7 | 14 | 30 = 0
  channel = 'all'
  versionFilter = DEFAULT_VERSION_FILTER
  appVersions: string[] = []
  dateRange: string[] = []
  // 「初始小说」下拉数据源：所选注册日期范围内实际出现过的初始小说及人数（随日期/渠道/版本联动刷新）
  initialBooks: InitialBookOption[] = []
  booksLoading = false
  private booksSeq = 0

  initData: PlatformNewData | null = null
  nonInitData: PlatformNewData | null = null
  allData: PlatformNewData | null = null
  // 查询序号：慢响应回来时若已有更新的查询，丢弃旧结果（防乱序渲染旧数据）
  private searchSeq = 0

  nDaysOptions = [
    { value: 0, label: '0日内（当天）' },
    { value: 1, label: '1日内' },
    { value: 3, label: '3日内' },
    { value: 7, label: '7日内' },
    { value: 14, label: '14日内' },
    { value: 30, label: '30日内' }
  ]

  channelOptions = [
    { value: 'toutiao', label: '头条' },
    { value: 'kuaishou', label: '快手' },
    { value: 'other', label: '其他' },
    { value: 'all', label: '所有' }
  ]

  // 指标分组（2026-07-14 语义定案：三列 = 互斥用户群拆分，指标为该人群跨全书的平台口径，
  // 与「平台新增数据」页同一套定义）。人数在列头展示，不设「新增」组（设备数/注册率对用户分组无意义）。
  metricGroups: { title: string; metrics: MetricDef[] }[] = [
    {
      title: '小说曝光 / 详情页',
      metrics: [
        { label: '小说列表曝光用户数', type: 'count', field: 'impression_users' },
        { label: '进入≥1本小说详情页率', type: 'rate', num: 'detail_view_ge1', den: 'new_users' },
        { label: '进入≥2本小说详情页率', type: 'rate', num: 'detail_view_ge2', den: 'new_users' },
        { label: '进入≥3本小说详情页率', type: 'rate', num: 'detail_view_ge3', den: 'new_users' }
      ]
    },
    {
      title: '进入剧情',
      metrics: [
        { label: '进入≥1本小说剧情内率', type: 'rate', num: 'enter_ge1', den: 'new_users' },
        { label: '进入≥2本小说剧情内率', type: 'rate', num: 'enter_ge2', den: 'new_users' },
        { label: '进入≥3本小说剧情内率', type: 'rate', num: 'enter_ge3', den: 'new_users' }
      ]
    },
    {
      title: '节点完成',
      metrics: [
        { label: '完成≥1个节点率', type: 'rate', num: 'node_ge1', den: 'new_users' },
        { label: '完成≥2个节点率', type: 'rate', num: 'node_ge2', den: 'new_users' },
        { label: '完成≥3个节点率', type: 'rate', num: 'node_ge3', den: 'new_users' }
      ]
    },
    {
      title: 'AI节点完成',
      metrics: [
        { label: '完成≥1个AI节点率', type: 'rate', num: 'ai_node_ge1', den: 'new_users' },
        { label: '完成≥2个AI节点率', type: 'rate', num: 'ai_node_ge2', den: 'new_users' },
        { label: '完成≥3个AI节点率', type: 'rate', num: 'ai_node_ge3', den: 'new_users' }
      ]
    },
    {
      title: '章节完成',
      metrics: [
        { label: '完成任意1本小说第一章率', type: 'rate', num: 'chapter1_book_ge1', den: 'new_users' },
        { label: '完成任意2本小说第一章率', type: 'rate', num: 'chapter1_book_ge2', den: 'new_users' },
        { label: '完成任意1本小说第二章率', type: 'rate', num: 'chapter2_book_ge1', den: 'new_users' },
        { label: '完成任意2本小说第二章率', type: 'rate', num: 'chapter2_book_ge2', den: 'new_users' },
        { label: '完成任意1本小说第三章率', type: 'rate', num: 'chapter3_book_ge1', den: 'new_users' },
        { label: '完成任意2本小说第三章率', type: 'rate', num: 'chapter3_book_ge2', den: 'new_users' }
      ]
    },
    {
      title: '付费（能量消费）',
      metrics: [
        { label: '付费率', type: 'rate', num: 'pay0', den: 'new_users' },
        { label: '付费10能量率', type: 'rate', num: 'pay10', den: 'new_users' },
        { label: '付费20能量率', type: 'rate', num: 'pay20', den: 'new_users' },
        { label: '付费30能量率', type: 'rate', num: 'pay30', den: 'new_users' },
        { label: '付费50能量率', type: 'rate', num: 'pay50', den: 'new_users' },
        { label: '付费100能量率', type: 'rate', num: 'pay100', den: 'new_users' },
        { label: '付费300能量率', type: 'rate', num: 'pay300', den: 'new_users' }
      ]
    },
    {
      title: '看广告充值',
      metrics: [
        { label: '看广告充值率', type: 'rate', num: 'ad_reward_users', den: 'new_users' },
        { label: '看广告充值60货币率', type: 'rate', num: 'ad_reward_60', den: 'new_users' }
      ]
    },
    {
      title: '支付充值',
      metrics: [
        { label: '支付充值率', type: 'rate', num: 'recharge_users', den: 'new_users' },
        { label: '支付充值5元率', type: 'rate', num: 'recharge_5yuan', den: 'new_users' }
      ]
    }
  ]

  headerCellStyle = { background: '#f5f7fa', color: '#303133', fontWeight: 600 }

  // 下拉版本列表：后端语义降序；接口失败/未含默认版本（未上报）时把默认版本补到最前，保证默认值可见可选
  get versionOptionList(): string[] {
    return this.appVersions.includes(DEFAULT_VERSION) ? this.appVersions : [DEFAULT_VERSION, ...this.appVersions]
  }

  // 口径说明里的当前所选版本文案，如「 ≥ 1.0.16」/「 = 1.0.16」
  get versionTipText(): string {
    const [mode, ver] = this.versionFilter.split(':')
    return mode === 'eq' ? ` = ${ver}` : ` ≥ ${ver}`
  }

  created() {
    // dateRange 赋值会触发 @Watch('dateRange') → 首次加载初始小说列表，无需显式再调
    this.dateRange = this.defaultRange()
    this.loadVersions()
  }

  async loadVersions() {
    try {
      this.appVersions = await getAppVersions()
    } catch (error) {
      // 拉不到就用 versionOptionList 的默认兜底项
      console.error('获取版本列表失败:', error)
    }
  }

  // 日期/渠道/版本任一变化 → 刷新「该口径下实际出现过的初始小说」列表
  @Watch('dateRange')
  @Watch('channel')
  @Watch('versionFilter')
  onBookFilterChange() {
    this.loadInitialBooks()
  }

  async loadInitialBooks() {
    if (!this.dateRange || this.dateRange.length !== 2) return
    this.booksLoading = true
    const seq = ++this.booksSeq
    try {
      const [mode, ver] = this.versionFilter.split(':')
      const books = await getInitialBooks({
        start_date: this.dateRange[0],
        end_date: this.dateRange[1],
        channel: this.channel as InitialBookNewDataParams['channel'],
        version: ver,
        version_mode: mode as InitialBookNewDataParams['version_mode']
      })
      if (seq !== this.booksSeq) return // 已有更新的加载，丢弃旧结果
      this.initialBooks = books
      // 当前选中的书不在新窗口内 → 清空，避免查询一个必然全 0 的初始群
      if (this.bookId && !books.some(b => b.game_id === this.bookId)) {
        this.bookId = null
      }
    } catch (error) {
      console.error('获取初始小说列表失败:', error)
    } finally {
      if (seq === this.booksSeq) {
        this.booksLoading = false
      }
    }
  }

  // 默认日期范围：今天的 -1 ~ -7 天
  defaultRange(): string[] {
    const today = new Date()
    const end = new Date(today)
    end.setDate(end.getDate() - 1)
    const start = new Date(today)
    start.setDate(start.getDate() - 7)
    return [this.fmt(start), this.fmt(end)]
  }

  fmt(d: Date): string {
    const y = d.getFullYear()
    const m = String(d.getMonth() + 1).padStart(2, '0')
    const day = String(d.getDate()).padStart(2, '0')
    return `${y}-${m}-${day}`
  }

  formatPercent(value: number): string {
    if (value === undefined || value === null || isNaN(value)) return '-'
    return (value * 100).toFixed(1) + '%'
  }

  formatCount(value: number): string {
    if (value === undefined || value === null) return '-'
    return value.toLocaleString()
  }

  rate(numerator: number, denominator: number): number {
    if (!denominator) return NaN
    return numerator / denominator
  }

  // 单元格主值：count → 人数；rate → 百分比
  mainValue(m: MetricDef, d: PlatformNewData | null): string {
    if (!d) return '-'
    if (m.type === 'count') {
      return this.formatCount(d[m.field as keyof PlatformNewData])
    }
    return this.formatPercent(this.rate(d[m.num as keyof PlatformNewData], d[m.den as keyof PlatformNewData]))
  }

  // 率值附带的分子人数小字，便于核对
  subCount(m: MetricDef, d: PlatformNewData | null): string {
    if (!d || m.type !== 'rate') return ''
    return `${this.formatCount(d[m.num as keyof PlatformNewData])} 人`
  }

  // 列头附带该用户群人数（新增用户数不再单独成组展示）
  colCount(d: PlatformNewData | null): string {
    if (!d) return ''
    return ` · ${this.formatCount(d.new_users)}人`
  }

  valueClass(m: MetricDef): string {
    return m.type === 'rate' ? 'cell-rate' : 'cell-count'
  }

  async handleSearch() {
    if (!this.bookId) {
      this.$message.warning('请先选择初始小说')
      return
    }
    if (!this.dateRange || this.dateRange.length !== 2) {
      this.$message.warning('请选择注册日期范围')
      return
    }
    this.loading = true
    const seq = ++this.searchSeq
    try {
      const [mode, ver] = this.versionFilter.split(':')
      const resp = await getInitialBookNewData({
        initial_game_id: this.bookId,
        n_days: this.nDays,
        start_date: this.dateRange[0],
        end_date: this.dateRange[1],
        channel: this.channel as InitialBookNewDataParams['channel'],
        version: ver,
        version_mode: mode as InitialBookNewDataParams['version_mode']
      })
      if (seq !== this.searchSeq) return // 已有更新的查询，丢弃本次旧结果
      this.initData = resp.init
      this.nonInitData = resp.non_init
      this.allData = resp.all
    } catch (error) {
      // 错误已由 serviceGo 响应拦截器统一弹窗
      console.error('获取初始小说新增数据失败:', error)
    } finally {
      if (seq === this.searchSeq) {
        this.loading = false
      }
    }
  }

  handleReset() {
    this.nDays = 0
    this.channel = 'all'
    this.versionFilter = DEFAULT_VERSION_FILTER
    this.dateRange = this.defaultRange()
    if (this.bookId) {
      this.handleSearch()
    }
  }
}
</script>

<style scoped>
.initial-book-new-data-wrapper {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

.filter-section {
  margin-bottom: 20px;
}

.filter-content {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 15px;
}

.filter-item {
  display: flex;
  align-items: center;
}

.filter-label {
  margin-right: 8px;
  font-size: 14px;
  color: #606266;
  white-space: nowrap;
}

.filter-tip {
  margin-top: 12px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}

.result-section {
  margin-bottom: 20px;
}

.section-title {
  margin: 0 0 15px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  border-left: 4px solid #409eff;
  padding-left: 10px;
}

.metric-groups {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.metric-group {
  background: #fff;
  border-radius: 8px;
  padding: 14px 16px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.group-title {
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  border-left: 3px solid #409eff;
  padding-left: 8px;
  line-height: 1.2;
}

.metric-name {
  color: #606266;
}

.col-head-init {
  color: #409eff;
}

.col-head-noninit {
  color: #67c23a;
}

.cell-rate {
  color: #409eff;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.cell-count {
  color: #303133;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.cell-sub {
  margin-left: 6px;
  font-size: 12px;
  color: #c0c4cc;
}

.metric-group ::v-deep .el-table {
  font-size: 13px;
}

.metric-group ::v-deep .el-table td {
  padding: 7px 0;
}

.empty-tip {
  padding: 40px;
  text-align: center;
  color: #909399;
  background: #fff;
  border-radius: 4px;
}
</style>
